import { COUNTRY_CODE } from '../../constants'
import { postcodeValidator } from 'postcode-validator'
import moment from 'moment'

export const required = value =>
  value === undefined || value === null || value === '' ? 'Required' : undefined

export const zeroRequired = value =>
  value || value === 0 ? undefined : 'Required'
export const greaterThanZero = value =>
  value && value > 0 ? undefined : `Required`
export const number = value =>
  value && isNaN(Number(value)) ? 'Must be a number' : undefined

export const sumMustBe = (fields, sumValue) => (value, allValues) => {
  const sum = fields.reduce((s, f) => s + parseFloat(allValues[f], 10), 0)
  return sum !== sumValue ? 'The sum must be 100%' : undefined
}

export const email = value =>
  value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
    ? 'Invalid email address'
    : undefined

export const betweenNumbers = (min, max) => value =>
  value >= min && value <= max
    ? undefined
    : `The value must be between ${min} - ${max}`

export const minValue = (
  minValue,
  label = `The min value is ${minValue}`,
) => value => (value && value.trim().length >= minValue ? undefined : label)

export const maxValue = (
  maxValue,
  label = `The max value is ${maxValue}`,
) => value => (value && value.trim().length <= maxValue ? undefined : label)

export const string = value =>
  value && /^[A-z]+$/gi.test(value) ? undefined : 'Must be a string'

export const postalCode = value =>
  value && /^\d+$/.test(value) && value.length === 5
    ? undefined
    : 'Must be a valid postal code'

export const stringLatinCharactersAndQuotes = value =>
  value && /^[ A-Za-z0-9\u00C0-\u024F\u1E00-\u1EFF,'".-]+$/u.test(value)
    ? undefined
    : 'Only Latin characters, numbers, and basic punctuation are allowed'

export const houseNumber = value =>
  value && /^[0-9 A-Za-z\u00C0-\u024F\u1E00-\u1EFF'"]+$/u.test(value)
    ? undefined
    : 'Only latin characters and numbers are allowed'

export const validatePhoneNumber = value =>
  value && /^[0-9 ()+]*$/.test(value)
    ? undefined
    : 'Only digits, space and, “()+”'

/**
 * Check input to be a string of comma-separated numbers.
 *  Returns undefined for valid value, or string with error message for invalid one
 * @example
 * '1,2'// valid
 * '1'// valid
 * '' //valid
 * ',,,' //valid
 * '1,2,cat' // invalid
 * @param {string} value value to be validate
 * @returns {string|undefined}
 */
export const commaSeparatedNumbers = value =>
  value &&
  value
    .split(',')
    .filter(num => num.length > 0)
    .every(num => !isNaN(num))
    ? undefined
    : 'Must be a comma-separated list of numbers'

/**
 * Check input to be a string of comma-separated values with at least 1 value.
 *  Returns undefined for valid value, or string with error message for invalid one
 * @example
 * `foo,bar`// valid
 * `,,foo` //valid
 * `` // invalid
 * `,,,` //invalid
 * @param {string} value value to be validate
 * @returns {string|undefined}
 */
export const requiredCommaSeparatedValues = value =>
  value && value.split(',').filter(val => val.length > 0).length > 0
    ? undefined
    : 'Required'

export const minNumberValue = minValue => value =>
  value && value >= minValue ? undefined : `The min value is ${minValue}`

export const roundNumber = number =>
  number && String(number).includes('.') ? `Round numbers only` : undefined

export const allowOnlyPositiveNumbers = value => {
  if (isNaN(value) || typeof value !== 'number') {
    return 1
  }
  if (value < 1) {
    return 1
  }
  return value
}

export const validateBirthDate = (
  value,
  countryCode,
  dateFormat = 'DD-MM-YYYY',
) => {
  const countryNamesMap = {
    [COUNTRY_CODE.CANADA]: 'Canada',
    [COUNTRY_CODE.UNITED_STATES]: 'United States',
    [COUNTRY_CODE.GERMANY]: 'Germany',
    [COUNTRY_CODE.AUSTRALIA]: 'Australia',
    [COUNTRY_CODE.NEW_ZEALAND]: 'New Zealand',
    [COUNTRY_CODE.BELGIUM]: 'Belgium',
    [COUNTRY_CODE.NETHERLANDS]: 'Netherlands',
    [COUNTRY_CODE.SPAIN]: 'Spain',
    [COUNTRY_CODE.FRANCE]: 'France',
    [COUNTRY_CODE.UNITED_KINGDOM]: 'United Kingdom',
    [COUNTRY_CODE.POLAND]: 'Poland',
    [COUNTRY_CODE.LUXEMBOURG]: 'Luxembourg',
    [COUNTRY_CODE.MEXICO]: 'Mexico',
    [COUNTRY_CODE.ITALY]: 'Italy',
    [COUNTRY_CODE.PORTUGAL]: 'Portugal',
    [COUNTRY_CODE.ARGENTINA]: 'Argentina',
    [COUNTRY_CODE.BRAZIL]: 'Brazil',
    [COUNTRY_CODE.IRELAND]: 'Ireland',
    [COUNTRY_CODE.SWITZERLAND]: 'Switzerland',
    [COUNTRY_CODE.AZEROTH]: 'Azeroth',
  }
  const countryToAgeMap = {
    [COUNTRY_CODE.CANADA]: 18,
    [COUNTRY_CODE.UNITED_STATES]: 18,
    [COUNTRY_CODE.GERMANY]: 18,
    [COUNTRY_CODE.AUSTRALIA]: 16,
    [COUNTRY_CODE.NEW_ZEALAND]: 16,
    [COUNTRY_CODE.BELGIUM]: 16,
    [COUNTRY_CODE.NETHERLANDS]: 16,
    [COUNTRY_CODE.UNITED_KINGDOM]: 16,
    [COUNTRY_CODE.SPAIN]: 18,
    [COUNTRY_CODE.FRANCE]: 18,
    [COUNTRY_CODE.POLAND]: 16,
    [COUNTRY_CODE.LUXEMBOURG]: 16,
    [COUNTRY_CODE.MEXICO]: 18,
    [COUNTRY_CODE.ITALY]: 16,
    [COUNTRY_CODE.PORTUGAL]: 18,
    [COUNTRY_CODE.ARGENTINA]: 18,
    [COUNTRY_CODE.BRAZIL]: 18,
    [COUNTRY_CODE.IRELAND]: 18,
    [COUNTRY_CODE.SWITZERLAND]: 18,
    [COUNTRY_CODE.AZEROTH]: 18,
  }

  const countryCodesWithoutValidations = [COUNTRY_CODE.MEXICO]

  if (
    !value ||
    countryCodesWithoutValidations.includes(countryCode) ||
    !countryCode
  ) {
    return undefined // No error if there's no value
  }

  const countryName = countryNamesMap[countryCode]
  const minimumAge = countryToAgeMap[countryCode]

  const isAgeValid =
    value &&
    moment().diff(moment(value, dateFormat || 'DD-MMMM-YYYY'), 'years') >=
      minimumAge

  if (!isAgeValid) {
    return `The BA must be at least ${minimumAge} years old to work in ${countryName}.`
  }
  return undefined
}

export const validateBmeuZipcode = value => {
  if (!value || typeof value !== 'string' || (value && value.length !== 9)) {
    return false
  }
  const zip = value.substring(0, 5)
  return postcodeValidator(zip, 'US')
}

export const validateMailerId = value => {
  if (
    !value ||
    isNaN(Number(value)) ||
    (value && value.length !== 9 && value.length !== 6)
  ) {
    return false
  }
  return true
}

export const validateBusinessNumber = countryCode => value => {
  if (
    countryCode === COUNTRY_CODE.PORTUGAL &&
    value &&
    !value.startsWith(COUNTRY_CODE.PORTUGAL)
  ) {
    return "Field must start with 'PT'"
  }
  return undefined
}
